import { Fragment, forwardRef, useMemo } from 'react'
import classNames from 'classnames'
import { Listbox, Transition } from '@headlessui/react'
import { CheckCircleIcon, ChevronDownIcon } from '@heroicons/react/24/outline'

import { InputOption, SelectInputProps } from 'types/ui'
import { StatusSymbol } from './status-symbol'
import { ForwardedRef } from 'react'

export const SelectInput = forwardRef(function SelectInput<T extends InputOption = InputOption>(
  {
    id,
    name,
    value,
    options,
    onChange,
    error,
    warning,
    success,
    disabled,
    className,
    bordered = true,
    chevron = true,
    placeholder = 'Select',
  }: SelectInputProps<T>,
  ref: ForwardedRef<HTMLButtonElement>
) {
  const listboxClassname = useListboxClassname({
    className,
    bordered,
    success,
    error,
    warning,
  })

  return (
    <Listbox name={name} value={value} onChange={onChange} disabled={disabled}>
      {({ open }) => (
        <div className={classNames(className, 'relative w-full')}>
          <Listbox.Button
            id={id}
            className={listboxClassname}
            data-testid="listbox.button"
            ref={ref}
          >
            {!value ? (
              <span className="max-w-full font-thin truncate text-ellipsis text-zinc-500 dark:text-zinc-400">
                {placeholder}
              </span>
            ) : (
              <span className="max-w-full truncate text-ellipsis normal-case">{value.name}</span>
            )}
            {chevron && (
              <div className="flex items-center justify-end space-x-2">
                {!disabled && <StatusSymbol error={error} warning={warning} success={success} />}
                {chevron && (
                  <ChevronDownIcon
                    className={classNames(
                      'w-3 h-3 transition-all ease-out duration-150 stroke-[3px] stroke-foreground dark:stroke-dark-foreground',
                      'rotate-0 ui-disabled:rotate-0 ui-disabled:stroke-zinc-500 dark:ui-disabled:stroke-zinc-400 ui-open:rotate-180'
                    )}
                  />
                )}
              </div>
            )}
          </Listbox.Button>
          <Transition
            show={open}
            as={Fragment}
            enter="transition duration-150 ease-out"
            enterFrom="transform -translate-y-2 opacity-0"
            enterTo="transform translate-y-0 opacity-100"
            leave="transition duration-150 ease-out"
            leaveFrom="transform translate-y-0 opacity-100"
            leaveTo="transform -translate-y-2 opacity-0"
          >
            <Listbox.Options
              className={classNames(
                'z-[1] min-w-[44px] bg-stone-100 dark:bg-stone-800 absolute left-0 right-0 mx-0 mt-1 overflow-clip overflow-y-auto h-fit max-h-60 border input-menu-border rounded popover-shadow cursor-auto'
              )}
            >
              {options.map((option) => (
                <Listbox.Option
                  key={option.id}
                  value={option}
                  disabled={option.unavailable}
                  className="input-menu-item"
                >
                  <span className="max-h-[52px] line-clamp-2 overflow-clip normal-case">
                    {option.name}
                  </span>
                  {chevron && (
                    <CheckCircleIcon className="input-menu-item-icon min-w-[14px] min-h-[14px]" />
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  )
})

function useListboxClassname({
  className,
  bordered,
  error,
  success,
  warning,
}: {
  className?: string
  bordered?: boolean
  error?: boolean
  success?: boolean
  warning?: boolean
}) {
  return useMemo(
    () =>
      classNames(
        className,
        'input-styles overflow-x-clip truncate listbox-button flex items-center justify-between text-base text-basic py-[5px] px-3 space-x-2',
        {
          '!border-none !shadow-none': !bordered,
          'state-error': error,
          'state-warning': warning && !error,
          'state-success': success && !(error || warning),
          'state-primary': !(error || warning || success),
        }
      ),
    [bordered, className, error, success, warning]
  )
}
